import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase'; // Ensure auth is exported from your firebase.js
import { 
  collection, 
  addDoc, 
  onSnapshot, 
  query, 
  orderBy, 
  where,
  deleteDoc, 
  doc, 
  updateDoc,
  Timestamp 
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import Swal from 'sweetalert2';

function TankConfiguration() {
  // Form States
  const [tankName, setTankName] = useState("");
  const [fuelType, setFuelType] = useState("MS");
  const [capacity, setCapacity] = useState("");
  const [openingStock, setOpeningStock] = useState(""); 
  
  // Auth & System States
  const [userId, setUserId] = useState(null);
  const [tankList, setTankList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Edit States
  const [editId, setEditId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
  });

  // 1. Monitor Auth State
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
        setTankList([]);
      }
      setAuthLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);

  // 2. Fetch User-Specific Tanks
  useEffect(() => {
    if (!userId) return;

    // Filter by userId to ensure data privacy
    const q = query(
      collection(db, "tanks"), 
      where("userId", "==", userId), 
      orderBy("created", "asc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const arr = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTankList(arr);
    }, (error) => {
      console.error("Firestore Error:", error);
    });

    return () => unsubscribe();
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) return Toast.fire({ icon: 'error', title: 'Session Expired' });
    if (!tankName || !capacity || !openingStock) {
      return Toast.fire({ icon: 'warning', title: 'Please fill all fields' });
    }

    setLoading(true);
    try {
      const tankData = {
        tankName: tankName.toUpperCase(),
        fuelType: fuelType,
        capacity: parseFloat(capacity),
        currentStock: parseFloat(openingStock),
        updatedAt: Timestamp.now(),
        userId: userId // Save the owner ID
      };

      if (isEditing) {
        await updateDoc(doc(db, "tanks", editId), tankData);
        Toast.fire({ icon: 'success', title: 'Tank Updated' });
      } else {
        await addDoc(collection(db, "tanks"), {
          ...tankData,
          created: Timestamp.now()
        });
        Toast.fire({ icon: 'success', title: 'Tank Added' });
      }

      resetForm();
    } catch (error) {
      Toast.fire({ icon: 'error', title: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (tank) => {
    setIsEditing(true);
    setEditId(tank.id);
    setTankName(tank.tankName);
    setFuelType(tank.fuelType);
    setCapacity(tank.capacity);
    setOpeningStock(tank.currentStock);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditId(null);
    setTankName("");
    setCapacity("");
    setOpeningStock("");
    setFuelType("MS");
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Tank?',
      text: "This will remove all inventory tracking for this tank!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete'
    });

    if (result.isConfirmed) {
      try {
        await deleteDoc(doc(db, "tanks", id));
        Toast.fire({ icon: 'success', title: 'Tank Removed' });
      } catch (err) {
        Toast.fire({ icon: 'error', title: 'Failed to delete' });
      }
    }
  };

  // Auth Guard Rendering
  if (authLoading) return (
    <div className="content-wrapper d-flex align-items-center justify-content-center">
      <div className="spinner-border text-primary" role="status"></div>
    </div>
  );

  if (!userId) return (
    <div className="content-wrapper p-5">
      <div className="alert alert-warning text-center">
        <h4><i className="icon fas fa-exclamation-triangle"></i> Access Denied</h4>
        Please login to manage your underground tanks.
      </div>
    </div>
  );

  return (
    <div className="content-wrapper">
      <section className="content-header">
        <div className="container-fluid">
          <h1>Underground Tank Configuration</h1>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            
            {/* FORM SECTION */}
            <div className="col-md-4">
              <div className={`card ${isEditing ? 'card-warning' : 'card-primary'} shadow`}>
                <div className="card-header">
                  <h3 className="card-title">{isEditing ? 'Edit Tank' : 'Add New Tank'}</h3>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="card-body">
                    <div className="form-group">
                      <label>Tank Name/No.</label>
                      <input type="text" className="form-control" value={tankName} 
                        onChange={(e) => setTankName(e.target.value)} placeholder="e.g. Tank No. 1" />
                    </div>
                    
                    <div className="form-group">
                      <label>Fuel Type</label>
                      <select className="form-control" value={fuelType} onChange={(e) => setFuelType(e.target.value)}>
                        <option value="MS">MS (Petrol)</option>
                        <option value="HSD">HSD (Diesel)</option>
                        <option value="POWER">Power</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Total Capacity (Liters)</label>
                      <input type="number" className="form-control" value={capacity} 
                        onChange={(e) => setCapacity(e.target.value)} placeholder="e.g. 20000" />
                    </div>

                    <div className="form-group">
                      <label>Current Stock (Liters)</label>
                      <input type="number" className="form-control" value={openingStock} 
                        onChange={(e) => setOpeningStock(e.target.value)} placeholder="Available Fuel" />
                    </div>
                  </div>
                  <div className="card-footer">
                    <button type="submit" disabled={loading} className={`btn btn-block ${isEditing ? 'btn-warning' : 'btn-primary'}`}>
                      {loading ? 'Wait...' : isEditing ? 'UPDATE TANK' : 'SAVE TANK'}
                    </button>
                    {isEditing && <button type="button" onClick={resetForm} className="btn btn-default btn-block mt-2">Cancel</button>}
                  </div>
                </form>
              </div>
            </div>

            {/* LIST SECTION */}
            <div className="col-md-8">
              <div className="card shadow">
                <div className="card-header">
                  <h3 className="card-title">Tank Inventory Overview</h3>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover table-striped mb-0">
                      <thead>
                        <tr>
                          <th>Tank Name</th>
                          <th>Fuel</th>
                          <th>Capacity</th>
                          <th>Current Stock</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tankList.map((tank) => {
                          const stockPercent = (tank.currentStock / tank.capacity) * 100;
                          return (
                            <tr key={tank.id}>
                              <td><b>{tank.tankName}</b></td>
                              <td>{tank.fuelType}</td>
                              <td>{tank.capacity?.toLocaleString()} L</td>
                              <td>{tank.currentStock?.toLocaleString()} L</td>
                              <td style={{ minWidth: '120px' }}>
                                <div className="progress progress-xs">
                                  <div className={`progress-bar ${stockPercent < 20 ? 'bg-danger' : 'bg-success'}`} 
                                    style={{ width: `${Math.min(stockPercent, 100)}%` }}></div>
                                </div>
                                <small className="text-muted">{stockPercent.toFixed(1)}% Full</small>
                              </td>
                              <td>
                                <button onClick={() => handleEdit(tank)} className="btn btn-xs btn-info mr-2">
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button onClick={() => handleDelete(tank.id)} className="btn btn-xs btn-danger">
                                  <i className="fas fa-trash"></i>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {tankList.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center py-4">No tanks configured yet.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}

export default TankConfiguration;